<template>
	<section
		class="new-event-hotel-reservation"
		:class="{'new-event-hotel-reservation--wide': showWideSearch}"
	>
		<wait-modal id="new-hotel-wait-modal" ref="waitModal" :title="waitModalTitle"></wait-modal>
		<conclusion-modal id="new-hotel-conclusion-modal" ref="conclusionModal"></conclusion-modal>
		<invalid-credit-card-modal ref="invalidPaymentModal"></invalid-credit-card-modal>

		<div v-if="showWideSearch" class="new-event-hotel-reservation__search-band">
			<hotel-reservation
				:criteria="criteria"
				:initial-default-date="initialDefaultDate"
				:translation="translation"
				variant="wide"
				@change="handleSearchChange"
				@search="fetchPrices"
			/>
		</div>

		<div v-else class="new-event-hotel-reservation__body">
			<aside class="new-event-hotel-reservation__sidebar">
				<div class="new-event-hotel-reservation__desktop-search">
					<hotel-reservation
						:criteria="criteria"
						:initial-default-date="initialDefaultDate"
						:translation="translation"
						variant="compact"
						@change="handleSearchChange"
						@search="fetchPrices"
					/>
				</div>

				<div v-if="showResults" class="new-event-hotel-reservation__mobile-search">
					<div
						v-if="!isMobileSearchExpanded"
						class="new-event-hotel-reservation__mobile-search-summary"
					>
						<h2>{{mobileSearchMessages.resultTitle}}</h2>
						<button
							type="button"
							class="new-event-hotel-reservation__mobile-search-link"
							@click="openMobileSearch"
						>
							<span>{{mobileSearchMessages.changeSearch}}</span>
							<span class="new-event-hotel-reservation__mobile-search-arrows" aria-hidden="true"></span>
						</button>
					</div>

					<hotel-reservation
						v-else
						:criteria="criteria"
						:initial-default-date="initialDefaultDate"
						:translation="translation"
						variant="wide"
						@change="handleSearchChange"
						@search="fetchPrices"
					/>
				</div>
			</aside>

			<div class="new-event-hotel-reservation__content">
				<div v-if="showResults && newHotelPrices.length" class="new-event-hotel-reservation__sort">
					<label for="new-hotel-order">{{translation.sortBy}}:</label>
					<div class="new-event-hotel-reservation__sort-select">
						<b-form-select
							id="new-hotel-order"
							v-model="hotelPriceOrder"
							:options="hotelPriceOrders"
						/>
					</div>
				</div>

				<div v-if="showResults" class="new-event-hotel-reservation__results">
					<room-type-price
						v-for="hotel in newHotelPrices"
						:key="hotel.id"
						:event-hotel="hotel"
						:event="event"
						:criteria="criteria"
						:countries="countries"
						:active-booking-key="activeBookingKey"
						@open-booking="openBooking"
						@close-booking="closeBooking"
						@file-request="buildFileRequest"
					/>

					<p v-if="noResult" class="new-event-hotel-reservation__no-results">
						{{translation.noResultsFound}}
					</p>
				</div>

				<hotel-checkout
					v-if="showCheckout"
					:booking-request="hotelBookingRequest"
					:pax="pax"
					:event="event"
					:booking-response="bookingResponse"
					:payment-method="paymentMethod"
					@back="returnToResults"
					@book="book"
					@handle-payment-method="handlePaymentMethod"
					@is-agreed-politics-terms="isAgreedPoliticsTerms"
				/>

				<ending-reservation
					v-if="showSuccess"
					:bank-ticket-url="bankTicketUrl"
					@select-option="$emit('select-option', $event)"
				/>
			</div>
		</div>
	</section>
</template>

<script>
import moment from 'moment';
import { translate } from '../../assets/translations';
import BlumarConfig from '../../blumar-config';
import BookingService from '../../services/BookingService';
import ConclusionModal from '../shared/ConclusionModal.vue';
import DestinationService from '../../services/DestinationService';
import EventService from '../../services/EventService';
import FileRequest from '../../model/FileRequest';
import HotelBooking from '../../model/HotelBooking';
import HotelPriceService from '../../services/HotelPricesService';
import HotelPriceSortType, { hotelPriceSortTypes } from '../../enums/HotelPriceSortType';
import InvalidCreditCardModal from '../shared/InvalidCreditCardModal.vue';
import HotelCheckout from './HotelCheckout.vue';
import RoomTypePrice from './RoomTypePrice.vue';
import HotelReservation from './HotelReservation.vue';
import EndingReservation from './EndingReservation.vue';
import PaymentMethods from '../../enums/PaymentMethods';
import PaymentService from '../../services/PaymentService';
import PaymentStatus from '../../enums/PaymentStatus';
import WaitModal from '../shared/WaitModal.vue';

export default {
	components: {
		ConclusionModal,
		InvalidCreditCardModal,
		HotelCheckout,
		RoomTypePrice,
		HotelReservation,
		EndingReservation,
		WaitModal,
	},
	props: {
		event: {
			type: Object,
			required: true,
		},
	},
	data() {
		return {
			activeBookingKey: '',
			bankTicketUrl: '',
			bookingResponse: null,
			bookingService: new BookingService(this.event.client),
			countries: [],
			criteria: {
				checkin: this.$route.params.checkin || null,
				checkout: this.$route.params.checkout || null,
				adultsQty: Number(this.$route.params.adultsQty) || 1,
				roomsQty: Number(this.$route.params.roomsQty) || 1,
				publishedRatesOnly: false
			},
			eventHotels: [],
			eventService: new EventService(),
			fileRequest: null,
			hasSearched: false,
			hotelBookingRequest: null,
			hotelPriceOrder: null,
			hotelPriceOrders: hotelPriceSortTypes,
			isMobileSearchExpanded: false,
			isFinished: false,
			newHotelPrices: [],
			noResult: false,
			pax: null,
			paymentMethod: null,
			paymentService: new PaymentService(),
			politicTerms: false,
			service: new HotelPriceService(),
		};
	},
	computed: {
		waitModalTitle() {
			return translate(this.$store.state.language).eventPage.optionLabels.hotelReservation;
		},
		initialDefaultDate() {
			if (this.event && this.event.startDate) {
				return moment(this.event.startDate, 'YYYY-MM-DD').format('DD/MM/YYYY');
			}
			return moment().format('DD/MM/YYYY');
		},
		showCheckout() {
			return Boolean(this.fileRequest && !this.isFinished);
		},
		showResults() {
			return this.hasSearched && !this.fileRequest && !this.isFinished;
		},
		showSuccess() {
			return this.isFinished;
		},
		showWideSearch() {
			return !this.hasSearched && !this.fileRequest && !this.isFinished;
		},
		translation() {
			return translate(this.$store.state.language).hotelReservationCard;
		},
		mobileSearchMessages() {
			return this.translation.mobileSearch || {};
		},
	},
	watch: {
		hotelPriceOrder() {
			this.sortHotelPrices();
		},
	},
	created() {
		this.getCountries();
	},
	mounted() {
		if (this.criteria.checkin && this.criteria.checkout) {
			this.fetchPrices(this.criteria);
		}
	},
	methods: {
		applySupplierPriority(hotels) {
			hotels.forEach(hotel => {
				const config = this.eventHotels.find(eventHotel => eventHotel.hotel.id === hotel.id);
				if (!config || !config.supplier) return;

				const preferredHasAvailable = hotel.roomTypes.some(roomType => {
					if (!roomType || !roomType.prices || !roomType.prices.length) return false;
					const price = roomType.prices[0];
					return price && price.supplierId === config.supplier.id && this.hasAvailability(price);
				});
				if (!preferredHasAvailable) return;

				hotel.roomTypes = hotel.roomTypes.filter(roomType => {
					if (!roomType || !roomType.prices || !roomType.prices.length) return false;
					const price = roomType.prices[0];
					return price && price.supplierId === config.supplier.id;
				});
			});
		},
		async authorizeSale(sale, hotelBooking) {
			sale.fileNumber = hotelBooking.fileNumber;
			this.setHotelPaymentAssociation(sale, hotelBooking);
			sale.companyId = hotelBooking.company.id;
			sale.amount = this.normalizeCurrencyValue(this.fileRequest.hotelBooking.price);
			sale.customer.name = this.fileRequest.pax.name;
			sale.customer.address.countryId = this.fileRequest.pax.country.id;
			return this.paymentService.authorize(sale);
		},
		buildFileRequest({ bookingRequest, pax }) {
			this.pax = pax;
			this.hotelBookingRequest = bookingRequest;
			this.fileRequest = new FileRequest(this.pax, new HotelBooking(this.hotelBookingRequest), null, 'hotel');
			this.bookingResponse = null;
			this.paymentMethod = null;
			this.politicTerms = false;
			this.$nextTick(() => {
				this.$el.scrollIntoView({ behavior: 'smooth', block: 'start' });
			});
		},
		cleanBookingState() {
			this.activeBookingKey = '';
			this.bankTicketUrl = '';
			this.bookingResponse = null;
			this.fileRequest = null;
			this.hotelBookingRequest = null;
			this.isFinished = false;
			this.pax = null;
			this.paymentMethod = null;
			this.politicTerms = false;
		},
		closeBooking() {
			this.activeBookingKey = '';
		},
		async book(sale) {
			if (!this.politicTerms) return;
			if (this.$refs.waitModal) this.$refs.waitModal.open();

			try {
				if (!this.bookingResponse) {
					this.bookingResponse = await this.bookingService.save(this.fileRequest);
				}
				const hotelBooking = this.bookingResponse && this.bookingResponse.data;

				if (sale.brandId === PaymentMethods.BANK_TICKET.id) {
					const bankTicketGenerateResponse = await this.generateBankTicket(sale, hotelBooking);
					if (
						!bankTicketGenerateResponse
						|| !bankTicketGenerateResponse.status
						|| Number(bankTicketGenerateResponse.status.id) === PaymentStatus.BANK_TICKET.NOT_ISSUED.id
					) {
						return this.$refs.invalidPaymentModal.open(PaymentMethods.BANK_TICKET);
					}

					await this.openBankTicket(bankTicketGenerateResponse);
					this.finishReservation();
					return;
				}

				const authorizedSale = await this.authorizeSale(sale, hotelBooking);
				if (!authorizedSale.data.status) {
					return this.$refs.invalidPaymentModal.open();
				}

				try {
					const confirmationResponse = await this.bookingService.confirm(hotelBooking.fileNumber);
					if (!confirmationResponse.data.fullyUpdated) {
						throw new Error();
					}
				} catch (error) {
					this.eventService.sendConfirmationErrorEmail(this.event.id, this.getErrorConfirmationRequest(hotelBooking));
				}

				this.finishReservation();
			} catch (error) {
				console.error(error);
				this.$refs.conclusionModal.open(false);
			} finally {
				if (this.$refs.waitModal) this.$refs.waitModal.close();
			}
		},
		async fetchPrices(criteria) {
			this.criteria = Object.assign({}, criteria || this.criteria);
			this.cleanBookingState();
			this.hasSearched = true;
			this.isMobileSearchExpanded = false;
			this.newHotelPrices = [];
			this.noResult = false;

			if (this.$refs.waitModal) this.$refs.waitModal.open();

			try {
				if (!this.criteria.checkin || !this.criteria.checkout) return;
				await this.getHotels();

				const searchCriteria = {
					...this.criteria,
					omnibeesResults: BlumarConfig.omnibeesEnabled,
					cityId: this.event.destination.id,
					hotelIds: this.eventHotels.map(eventHotel => eventHotel.hotel.id),
					checkin: moment(this.criteria.checkin).format('DD/MM/YYYY'),
					checkout: moment(this.criteria.checkout).format('DD/MM/YYYY'),
				};
				const response = await this.service.list(searchCriteria, this.event.client);
				const hotelPrices = this.filterHotelPrices(response.data);
				this.mergeHotelPrices(hotelPrices);
				this.noResult = !this.newHotelPrices.length;
				this.hotelPriceOrder = this.event.hotelOrder
					? HotelPriceSortType[this.event.hotelOrder]
					: HotelPriceSortType.LOWEST_PRICE;
				this.sortHotelPrices();
			} catch (error) {
				console.error(error);
				this.noResult = true;
			} finally {
				if (this.$refs.waitModal) this.$refs.waitModal.close();
			}
		},
		filterHotelPrices(responseHotelPrices) {
			const responseHotels = responseHotelPrices && responseHotelPrices.hotels ? responseHotelPrices.hotels : [];
			responseHotels.forEach(hotel => {
				hotel.roomTypes = (hotel.roomTypes || [])
					.filter(roomType => roomType.prices && roomType.prices.length)
					.map(roomType => Object.assign({}, roomType, {
						prices: [this.getMinPrice(roomType.prices)],
					}));
			});
			const eventHotelIds = this.eventHotels.map(eventHotel => eventHotel.hotel.id);
			const data = {
				hotels: responseHotels.filter(hotel => eventHotelIds.indexOf(hotel.id) !== -1 && hotel.roomTypes.length),
			};
			this.applySupplierPriority(data.hotels);
			return data;
		},
		finishReservation() {
			this.isFinished = true;
			this.fileRequest = null;
			this.activeBookingKey = '';
			this.$nextTick(() => {
				this.$el.scrollIntoView({ behavior: 'smooth', block: 'start' });
			});
		},
		generateBankTicket(sale, hotelBooking) {
			sale.companyId = hotelBooking.company.id;
			sale.fileNumber = hotelBooking.fileNumber;
			this.setHotelPaymentAssociation(sale, hotelBooking);
			sale.payerName = this.fileRequest.pax.name;
			sale.value = this.normalizeCurrencyValue(this.fileRequest.hotelBooking.price);
			return this.paymentService.generateBankTicket(sale)
				.then(response => response.data)
				.catch(error => {
					console.log(error.response ? error.response.data.message : error);
				});
		},
		async getCountries() {
			try {
				const response = await new DestinationService(this.event.client).getCountries();
				this.countries = response.data.sort((firstCountry, secondCountry) => {
					if (firstCountry.name > secondCountry.name) return 1;
					if (firstCountry.name < secondCountry.name) return -1;
					return 0;
				});
			} catch (error) {
				this.countries = [];
			}
		},
		getErrorConfirmationRequest(hotelBooking) {
			return {
				pax: this.pax,
				fileNumber: hotelBooking.fileNumber,
				paidValue: hotelBooking.price,
			};
		},
		getHotelPaymentAssociation(hotelBooking) {
			const bookings = (hotelBooking && hotelBooking.bookings) || [];
			const booking = bookings[0];
			if (bookings.length !== 1 || !booking || !booking.id) {
				throw new Error('A reserva de hotel não retornou o ID do booking.');
			}

			const rooms = booking.rooms || [];
			const room = rooms.length === 1 ? rooms[0] : null;
			if (room && room.id == null) {
				throw new Error('A reserva de hotel não retornou o ID do quarto.');
			}

			return {
				bookingId: booking.id,
				roomId: room ? room.id : null,
			};
		},
		setHotelPaymentAssociation(payment, hotelBooking) {
			const association = this.getHotelPaymentAssociation(hotelBooking);
			payment.bookingId = association.bookingId.toString();
			if (association.roomId != null) {
				payment.roomId = association.roomId.toString();
			} else {
				delete payment.roomId;
			}
		},
		async getHotels() {
			if (this.eventHotels.length) return;
			const hotelResponse = await this.eventService.getHotels(this.event.id);
			this.eventHotels = hotelResponse.data;
		},
		getMinPrice(prices) {
			return prices.reduce((accumulator, currentValue) => (
				this.normalizeCurrencyValue(accumulator.price) <= this.normalizeCurrencyValue(currentValue.price)
					? accumulator
					: currentValue
			), prices[0]);
		},
		handlePaymentMethod(paymentMethod) {
			this.paymentMethod = paymentMethod;
		},
		handleSearchChange(criteria) {
			this.criteria = Object.assign({}, criteria);
		},
		hasAvailability(price) {
			if (!price || !price.allotment) return false;
			return price.allotment.freeSale || price.allotment.availableUnits >= Number(this.criteria.roomsQty || 1);
		},
		hotelMinimumPrice(hotel) {
			const prices = [];
			(hotel.roomTypes || []).forEach(roomType => {
				(roomType.prices || []).forEach(price => {
					prices.push(this.normalizeCurrencyValue(price.price));
				});
			});
			return prices.length ? Math.min.apply(null, prices) : 0;
		},
		isAgreedPoliticsTerms(isAccept) {
			this.politicTerms = isAccept;
		},
		mergeHotelPrices(hotelPrices) {
			this.newHotelPrices = [];
			hotelPrices.hotels.forEach(hotelPrice => {
				const eventHotel = this.eventHotels.find(item => item.hotel.id === hotelPrice.id);
				if (!eventHotel) return;
				this.newHotelPrices.push(Object.assign({}, hotelPrice, {
					eventInfo: {
						displayOrder: eventHotel.displayOrder,
						eventDistance: eventHotel.eventDistance,
						stamp: eventHotel.stamp,
						translations: eventHotel.translations,
					},
				}));
			});
		},
		normalizeCurrencyValue(value) {
			if (value === null || value === undefined || value === '') return 0;
			if (typeof value === 'number') return Number.isFinite(value) ? value : 0;

			let normalizedValue = String(value).trim().replace(/[^\d,.-]/g, '');
			if (!normalizedValue) return 0;

			const lastComma = normalizedValue.lastIndexOf(',');
			const lastDot = normalizedValue.lastIndexOf('.');

			if (lastComma !== -1 && lastDot !== -1) {
				normalizedValue = lastComma > lastDot
					? normalizedValue.replace(/\./g, '').replace(',', '.')
					: normalizedValue.replace(/,/g, '');
			} else if (lastComma !== -1) {
				normalizedValue = normalizedValue.replace(',', '.');
			} else if (/^-?\d{1,3}(\.\d{3})+$/.test(normalizedValue)) {
				normalizedValue = normalizedValue.replace(/\./g, '');
			}

			const numberValue = Number(normalizedValue);
			return Number.isFinite(numberValue) ? numberValue : 0;
		},
		async openBankTicket(bankTicketGenerateResponse) {
			try {
				const response = await this.paymentService.getBankTicketDocument(
					bankTicketGenerateResponse.id,
					bankTicketGenerateResponse.payerName,
				);
				this.bankTicketUrl = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
				window.open(this.bankTicketUrl, '_blank');
			} catch (error) {
				console.log(error);
				alert('Houve um erro ao gerar o documento do boleto.');
			}
		},
		openBooking(bookingKey) {
			this.activeBookingKey = bookingKey;
			this.isMobileSearchExpanded = false;
			this.fileRequest = null;
			this.hotelBookingRequest = null;
			this.bookingResponse = null;
			this.paymentMethod = null;
			this.politicTerms = false;
		},
		openMobileSearch() {
			this.isMobileSearchExpanded = true;
		},
		returnToResults() {
			this.fileRequest = null;
			this.bookingResponse = null;
			this.paymentMethod = null;
			this.politicTerms = false;
		},
		sortHotelPrices() {
			if (!this.hotelPriceOrder) return;
			if (this.hotelPriceOrder === HotelPriceSortType.CUSTOM) {
				this.newHotelPrices.sort((a, b) => a.eventInfo.displayOrder - b.eventInfo.displayOrder);
			}
			if (this.hotelPriceOrder === HotelPriceSortType.EVENT_DISTANCE) {
				this.newHotelPrices.sort((a, b) => a.eventInfo.eventDistance - b.eventInfo.eventDistance);
			}
			if (this.hotelPriceOrder === HotelPriceSortType.STAR_RATING) {
				this.newHotelPrices.sort((a, b) => b.starRating - a.starRating);
			}
			if (this.hotelPriceOrder === HotelPriceSortType.HOTEL_NAME) {
				this.newHotelPrices.sort((a, b) => (a.name < b.name ? -1 : 1));
			}
			if (this.hotelPriceOrder === HotelPriceSortType.LOWEST_PRICE) {
				this.newHotelPrices.sort((a, b) => this.hotelMinimumPrice(a) - this.hotelMinimumPrice(b));
			}
			if (this.hotelPriceOrder === HotelPriceSortType.HIGHEST_PRICE) {
				this.newHotelPrices.sort((a, b) => this.hotelMinimumPrice(b) - this.hotelMinimumPrice(a));
			}
		},
	},
};
</script>

<style scoped lang="scss">
.new-event-hotel-reservation {
	background: #ffffff;
	color: #002c41;
}

.new-event-hotel-reservation__search-band {
	box-sizing: border-box;
	width: 100%;
	min-height: 360px;
	padding: 27px 0;
	background: #eeeeee;
}

.new-event-hotel-reservation__body {
	display: flex;
	gap: 37px;
	align-items: start;
	box-sizing: border-box;
	width: calc(100% - 196px);
	max-width: 1530px;
	margin: 0 auto 142px;
}

.new-event-hotel-reservation__sidebar {
	flex: 0 0 329px;
	position: sticky;
	top: 112px;
}

.new-event-hotel-reservation__mobile-search {
	display: none;
}

.new-event-hotel-reservation__mobile-search-summary {
	box-sizing: border-box;
	width: 100%;
	max-width: 366px;
	min-height: 96px;
	margin: 0 auto;
	padding: 20px 16px 17px;
	border-radius: 18px;
	background: #66acc6;
	color: #ffffff;
}

.new-event-hotel-reservation__mobile-search-summary h2 {
	margin: 0 0 13px;
	color: #ffffff;
	font-size: 20px;
	font-weight: 700;
	line-height: 25px;
}

.new-event-hotel-reservation__mobile-search-link {
	display: inline-flex;
	align-items: center;
	gap: 8px;
	padding: 0;
	border: 0;
	background: transparent;
	color: #ffffff;
	font-family: inherit;
	font-size: 14px;
	font-weight: 700;
	line-height: 25px;
	text-align: left;
	cursor: pointer;
}

.new-event-hotel-reservation__mobile-search-arrows {
	position: relative;
	display: inline-block;
	width: 17px;
	height: 11px;
}

.new-event-hotel-reservation__mobile-search-arrows::before,
.new-event-hotel-reservation__mobile-search-arrows::after {
	content: "";
	position: absolute;
	top: 2px;
	width: 8px;
	height: 8px;
	border-right: 3px solid #ffffff;
	border-bottom: 3px solid #ffffff;
	transform: rotate(-45deg);
}

.new-event-hotel-reservation__mobile-search-arrows::before {
	left: 0;
}

.new-event-hotel-reservation__mobile-search-arrows::after {
	left: 8px;
}

.new-event-hotel-reservation__content {
	flex: 1 1 auto;
	min-width: 0;
}

.new-event-hotel-reservation__sort {
	display: flex;
	align-items: center;
	justify-content: flex-end;
	gap: 18px;
	margin-bottom: 12px;
}

.new-event-hotel-reservation__sort label {
	margin: 0;
	color: #002c41;
	font-size: 16px;
	font-weight: 400;
	line-height: 19px;
}

.new-event-hotel-reservation__sort-select {
	position: relative;
	width: 252px;
}

.new-event-hotel-reservation__sort-select::after {
	content: "";
	position: absolute;
	top: 20px;
	right: 22px;
	width: 13px;
	height: 13px;
	border-right: 3px solid #66acc6;
	border-bottom: 3px solid #66acc6;
	pointer-events: none;
	transform: rotate(45deg) translateY(-4px);
}

.new-event-hotel-reservation__sort-select /deep/ .custom-select {
	box-sizing: border-box;
	width: 100%;
	height: 53px;
	padding: 0 58px 0 28px;
	border: 1px solid #d7d7d7;
	border-radius: 15px;
	background: #ffffff;
	background-image: none;
	color: #002c41;
	font-family: inherit;
	font-size: 16px;
	font-weight: 700;
	line-height: 19px;
	box-shadow: none;
	appearance: none;
}

.new-event-hotel-reservation__results {
	display: flex;
	flex-direction: column;
	gap: 21px;
}

.new-event-hotel-reservation__no-results {
	box-sizing: border-box;
	margin: 0;
	padding: 36px 52px;
	border: 1px solid #d7d7d7;
	border-radius: 35px;
	background: #ffffff;
	color: #002c41;
	font-size: 20px;
	font-weight: 700;
	line-height: 24px;
}

@media (max-width: 1100px) {
	.new-event-hotel-reservation__body {
		gap: 28px;
		width: 90%;
	}

	.new-event-hotel-reservation__sidebar {
		flex-basis: 280px;
	}

	.new-event-hotel-reservation__sidebar /deep/ .new-event-hotel-search--compact {
		width: 280px;
		padding-right: 30px;
		padding-left: 30px;
	}
}

@media (max-width: 900px) {
	.new-event-hotel-reservation__search-band {
		min-height: 0;
		padding: 24px 12px;
	}

	.new-event-hotel-reservation__body {
		flex-direction: column;
		gap: 18px;
		width: 90%;
		max-width: none;
		margin-bottom: 72px;
	}

	.new-event-hotel-reservation__sidebar {
		flex: 0 0 auto;
		position: static;
		width: 100%;
		max-width: none;
		margin: 0 auto;
	}

	.new-event-hotel-reservation__desktop-search {
		display: none;
	}

	.new-event-hotel-reservation__mobile-search {
		display: block;
		width: 100%;
	}

	.new-event-hotel-reservation__sidebar /deep/ .new-event-hotel-search--compact {
		width: 100%;
	}

	.new-event-hotel-reservation__sort {
		display: none;
	}

	.new-event-hotel-reservation__results,
	.new-event-hotel-reservation__content {
		width: 100%;
		max-width: none;
		margin: 0 auto;
	}
}

@media (max-width: 480px) {
	.new-event-hotel-reservation__body {
		width: calc(100% - 24px);
		max-width: 374px;
	}

	.new-event-hotel-reservation__sidebar,
	.new-event-hotel-reservation__results,
	.new-event-hotel-reservation__content {
		max-width: 366px;
	}
}
</style>



// WEBPACK FOOTER //
// src/components/hotel-reservation/HotelPrices.vue